import {i18n} from '@yourcoach/i18n';

import type {ConferenceApiModel} from '../../../api/conference';
import type {BNCoachConferencesStopParams} from '../../../api/types/bnrks/methods';
import type {CoreCoachConferencesStopParams} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig} from '../utils';

import {conferenceStore} from '../../../api/conference';
import InfoIcon from '../../../assets/icons/primary/Info Circle.svg';
import {alert} from '../../../components/alert';
import {ConferenceImage} from '../../../components/ConferenceImage';
import {msi, vsi} from '../../../styles/normalizeSize';
import {View} from '../../../uikit/View';
import {isMeConferenceHost} from '../../../utils/entity/conference';
import {isChatConference} from '../../../utils/entity/conference/isChatConference';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {AppError} from '../../../utils/errors/app';
import {logger} from '../../../utils/logger';
import {COMMAND} from '../utils';

export {conferenceStop};

type Config = BaseConfig<
  Omit<BNCoachConferencesStopParams | CoreCoachConferencesStopParams, '_id'>
> & {
  entity: ConferenceApiModel;
};

const strategy: AsyncStrategy = async (roles, config) => {
  return isMeCoachForEntity((config as Config).entity)
    ? roles.coach(config)
    : roles.client(config);
};

async function conferenceStop(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

conferenceStop.asClient = asClient;
conferenceStop.asCoach = asCoach;

async function asClient(_config: Config) {
  throw new AppError('not_allowed');
}

async function asCoach({entity, params}: Config) {
  if (!isMeConferenceHost(entity)) {
    throw new AppError('Only host can stop the conference');
  }

  if (await confirm(entity)) {
    const result = await conferenceStore.coach.stop(entity, params);

    return result;
  }

  return COMMAND.CANCEL;
}

const confirm = (conference: ConferenceApiModel) => {
  if (isChatConference(conference)) {
    return alert.show<boolean>({
      buttons: [
        {
          label: i18n.t('shared:yes_end_session'),
          onAwaitableClick: resolve => {
            logger.event('chat_session_end_confirm_tap');
            resolve(true);
          },
          scheme: 'primary',
        },
        {
          label: i18n.t('shared:no_close'),
          onAwaitableClick: resolve => {
            logger.event('chat_session_end_cancel_tap');
            resolve(false);
          },
        },
      ],
      icon: InfoIcon,
      iconColor: 'icon3',
      title: i18n.t('shared:are_you_sure'),
    });
  }

  return alert.show<boolean>({
    beforeTitleSlot: () => (
      <View
        style={{
          height: vsi(170),
          position: 'relative',
          top: msi(-20),
        }}
      >
        <ConferenceImage />
      </View>
    ),
    buttons: [
      {
        label: i18n.t(
          'shared:Conference.stop_conference_confirm_success_button',
        ),
        onAwaitableClick: resolve => resolve(true),
        scheme: 'error',
      },
      {
        label: i18n.t(
          'shared:Conference.stop_conference_confirm_cancel_button',
        ),
        onAwaitableClick: resolve => resolve(false),
      },
    ],
    title: i18n.t('shared:Conference.stop_conference_confirm_message'),
    variant: 'promo',
  });
};
